import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DanishDateField } from '@/components/ui/DanishDateField';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { ALLOWED_MIME_TYPES, validateDocumentFile } from '@/hooks/useDocuments';
import type { PatientFileCategoryRow } from '@/hooks/usePatientFileCategories';
import { FileCategoryPicker } from '@/components/documents/FileCategoryPicker';
import { localDateInputValue } from '@/utils/fileDisplayName';

export interface UploadFileDraft {
  file: File;
  categoryId: string | null;
  documentDate: string;
}

export function UploadFileSlideOver({
  open,
  onClose,
  onUpload,
  uploading,
  categories,
}: {
  open: boolean;
  onClose: () => void;
  onUpload: (files: File[], categoryId: string | null, documentDate: string) => Promise<void>;
  uploading: boolean;
  categories: readonly PatientFileCategoryRow[];
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<UploadFileDraft[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dragOver, setDragOver] = useState(false);
  const [activeName, setActiveName] = useState<string | null>(null);

  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const next = [...items];
    const nextErrors = { ...errors };
    for (const file of Array.from(list)) {
      const validationError = validateDocumentFile(file);
      if (validationError) {
        nextErrors[file.name] = t(`documents.${validationError}`);
      }
      if (!next.some((existing) => existing.file.name === file.name && existing.file.size === file.size)) {
        next.push({
          file,
          categoryId: null,
          documentDate: localDateInputValue(),
        });
      }
    }
    setItems(next);
    setErrors(nextErrors);
  };

  const updateItem = (file: File, patch: Partial<Pick<UploadFileDraft, 'categoryId' | 'documentDate'>>) => {
    setItems((current) =>
      current.map((item) => (item.file === file ? { ...item, ...patch } : item)),
    );
  };

  const handleClose = () => {
    setItems([]);
    setErrors({});
    onClose();
  };

  return (
    <SlideOver
      open={open}
      title={t('patients.files.upload')}
      description={t('patients.files.upload_description')}
      onClose={handleClose}
      dirty={items.length > 0}
      footer={
        <SlideOverFooter
          onCancel={handleClose}
          primary={
            <Button
              type="button"
              disabled={items.length === 0 || uploading}
              loading={uploading}
              onClick={() => {
                void (async () => {
                  for (const item of items) {
                    setActiveName(item.file.name);
                    await onUpload([item.file], item.categoryId, item.documentDate);
                  }
                  setActiveName(null);
                  handleClose();
                })();
              }}
            >
              {t('patients.files.upload_n', { count: items.length })}
            </Button>
          }
        />
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_MIME_TYPES.join(',')}
        multiple
        className="hidden"
        data-testid="patient-files-upload-input"
        onChange={(event) => addFiles(event.target.files)}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => {
          event.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          addFiles(event.dataTransfer.files);
        }}
        className={cn(
          'flex w-full flex-col items-center rounded-card border border-dashed px-s4 py-s7 text-center',
          dragOver ? 'border-ds-teal bg-ds-teal-tint' : 'border-ds-field bg-ds-sunk',
        )}
      >
        <Upload className="h-6 w-6 text-ds-teal" aria-hidden />
        <p className={cn(TYPE.section, 'mt-s3 text-ds-text')}>{t('patients.files.dropzone')}</p>
        <p className={cn(TYPE.small, 'mt-s1 text-ds-muted')}>{t('patients.files.dropzone_hint')}</p>
        <span className={cn(TYPE.control, 'mt-s4 text-ds-teal')}>{t('patients.files.choose')}</span>
      </button>

      {items.length > 0 ? (
        <ul className="mt-s4 flex flex-col gap-s3">
          {items.map((item) => (
            <li
              key={`${item.file.name}-${item.file.size}`}
              className="flex flex-col gap-s3 rounded-pop bg-ds-bg px-s3 py-s3"
              data-testid={`patient-files-upload-row-${item.file.name}`}
            >
              <div className="flex items-center gap-s3">
                <FileText className="h-5 w-5 text-ds-muted" aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className={cn(TYPE.body, 'truncate text-ds-text')}>{item.file.name}</p>
                  {errors[item.file.name] ? (
                    <p className={cn(TYPE.small, 'text-ds-red-ink')}>{errors[item.file.name]}</p>
                  ) : activeName === item.file.name ? (
                    <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patients.files.uploading')}</p>
                  ) : (
                    <p className={cn(TYPE.small, 'text-ds-muted')}>{t('patients.files.ready')}</p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t('patients.files.remove')}
                  onClick={() => setItems(items.filter((existing) => existing.file !== item.file))}
                >
                  <X />
                </Button>
              </div>
              <FileCategoryPicker
                id={`upload-category-${item.file.name}`}
                label={t('patients.files.category')}
                categories={categories}
                value={item.categoryId}
                onChange={(categoryId) => updateItem(item.file, { categoryId })}
              />
              <DanishDateField
                label={t('patients.files.document_date')}
                value={item.documentDate}
                onChange={(documentDate) =>
                  updateItem(item.file, { documentDate: documentDate || localDateInputValue() })
                }
              />
            </li>
          ))}
        </ul>
      ) : null}
    </SlideOver>
  );
}
